/*
 * -----------------------------------------------------------------------
 *  <copyright file="actions-menu-v2/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Grow from '@mui/material/Grow';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import cn from 'clsx';
import DarkTooltip from '../dark-tool-tip/dark-tool-tip';
import { IActionItemV2 } from '../expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { IActionsMenuPropsV2 } from './actions-menu-v2.interface';
import { useTranslation } from 'hooks';
import './styles.scss';
import { textToId } from '@utils/automation-test-helpers';

const ActionsMenuV2 = ({
    actions,
    transformOrigin,
    anchorOrigin,
    height,
    wrapperClassName,
    menuClassName,
    showSingleMenuItem,
    disabled,
}: IActionsMenuPropsV2) => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (item: IActionItemV2) => {
        handleClose();
        setTimeout(() => {
            if (item.enabled) {
                item.onClick(item);
            }
        }, 0);
    };

    if (actions.length === 1 && showSingleMenuItem) {
        const item = actions[0];
        return (
            <DarkTooltip title={t(item.label)} placement="top" arrow>
                <div className={cn('menu-wrapper-v2', wrapperClassName, { opened: anchorEl })}>
                    <Button
                        className={cn('btn-generic', { disabled: !item.enabled })}
                        key={item.label}
                        onClick={() => handleMenuItemClick(item)}
                        data-testid={item.testId ?? textToId(item.label)}
                    >
                        {item.icon && <FontAwesomeIcon icon={item.icon} className="generic-icon" />}
                    </Button>
                </div>
            </DarkTooltip>
        );
    }

    return (
        <div className={cn('menu-wrapper-v2', wrapperClassName, { opened: anchorEl }, { disabled })}>
            <Button
                aria-controls="actions-menu"
                aria-haspopup="false"
                aria-describedby="actions-menu"
                className="btn-dots"
                style={{ height }}
                onClick={handleClick}
                disabled={disabled}
                data-testid="actions-menu"
            >
                <FontAwesomeIcon icon={['fal', 'ellipsis-vertical']} size="lg" className="ellipsis-icon" />
            </Button>
            <Menu
                id="actions-menu"
                className={cn('menu-items-v2', menuClassName)}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slots={{ transition: Grow }}
                anchorOrigin={{ vertical: anchorOrigin.vertical, horizontal: anchorOrigin.horizontal }}
                transformOrigin={{ vertical: transformOrigin.vertical, horizontal: transformOrigin.horizontal }}
                slotProps={{
                    root: {
                        slotProps: {
                            backdrop: {
                                style: {
                                    backgroundColor: 'transparent',
                                },
                            },
                        },
                    },
                }}
            >
                {actions.map((item, index) => (
                    <MenuItem
                        className={cn('menu-item', { disabled: !item.enabled })}
                        key={item.label}
                        onClick={() => handleMenuItemClick(item)}
                        data-testid={`actions-menu-item-${textToId(item.label, index)}`}
                    >
                        {item.icon && (
                            <div className="menu-icon-wrap">
                                <FontAwesomeIcon icon={item.icon} className="menu-icon" />
                            </div>
                        )}
                        <Typography variant="inherit" className="label-menu">
                            {t(item.label)}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default ActionsMenuV2;
